import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, take } from 'rxjs';
import { AppRoutes, DrillScoring, HapticsService, SessionRoutes } from '@core';
import {
  SZ_SCORING_LABELS,
  SzAppShellComponent,
  SzButtonComponent,
  SzConfirmDialogService,
  SzCtaBarComponent,
  SzIconComponent,
  SzLiveDotComponent,
  SzScoringValue,
  SzSkeletonComponent,
  SzTopBarComponent
} from '@ui';
import { RunCommandHandler, SessionDraftQueryHandler, SessionDraftQueryResult } from '../../application/handlers';
import { SessionDrillViewModel } from '../../application/models';
import { SessionStore } from '../../application/state';
import { HitFactorRunEntryComponent } from '../run-entry/hit-factor-run-entry/hit-factor-run-entry.component';
import { RepsOnlyRunEntryComponent } from '../run-entry/reps-only-run-entry/reps-only-run-entry.component';
import { TimePerRepRunEntryComponent } from '../run-entry/time-per-rep-run-entry/time-per-rep-run-entry.component';
import { SessionProgressIndicatorComponent } from '../session-progress-indicator/session-progress-indicator.component';

@Component({
  selector: 'lib-session-active',
  templateUrl: './session-active.component.html',
  styleUrls: ['./session-active.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzButtonComponent,
    SzCtaBarComponent,
    SzIconComponent,
    SzLiveDotComponent,
    SzSkeletonComponent,
    SessionProgressIndicatorComponent,
    HitFactorRunEntryComponent,
    RepsOnlyRunEntryComponent,
    TimePerRepRunEntryComponent
  ],
  providers: [SessionDraftQueryHandler, RunCommandHandler]
})
export class SessionActiveComponent {
  private readonly router: Router = inject(Router);
  private readonly store: SessionStore = inject(SessionStore);
  private readonly haptics: HapticsService = inject(HapticsService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly draftHandler: SessionDraftQueryHandler = inject(SessionDraftQueryHandler);
  private readonly confirmDialog: SzConfirmDialogService = inject(SzConfirmDialogService);

  readonly scoring: typeof DrillScoring = DrillScoring;
  readonly scoringLabels: Readonly<Record<SzScoringValue, string>> = SZ_SCORING_LABELS;

  readonly draft: Signal<SessionDraftQueryResult | null | undefined> = toSignal(this.draftHandler.query());

  readonly sessionDrills: Signal<readonly SessionDrillViewModel[]> = computed(() => {
    const d: SessionDraftQueryResult | null | undefined = this.draft();
    return d == null ? [] : d.draft.sessionDrills;
  });

  readonly activeIndex: Signal<number> = computed(() => {
    const d: SessionDraftQueryResult | null | undefined = this.draft();
    return d == null ? 0 : d.draft.activeIndex;
  });

  readonly sessionDrill: Signal<SessionDrillViewModel | null> = computed(() => {
    const drills: readonly SessionDrillViewModel[] = this.sessionDrills();
    const index: number = this.activeIndex();
    return drills.length === 0 || index >= drills.length ? null : drills[index];
  });

  readonly isMultiDrill: Signal<boolean> = computed(() => this.sessionDrills().length > 1);

  readonly hasPreviousDrill: Signal<boolean> = computed(() => this.isMultiDrill() && this.activeIndex() > 0);

  readonly previousDrillName: Signal<string | null> = computed(() => {
    const idx: number = this.activeIndex();
    return idx <= 0 ? null : (this.sessionDrills()[idx - 1]?.drillName ?? null);
  });

  readonly currentRunIndex: Signal<number> = computed(() => {
    const sd: SessionDrillViewModel | null = this.sessionDrill();
    return sd === null ? 1 : sd.runs.length + 1;
  });

  readonly currentRunLabel: Signal<string> = computed(() => this.currentRunIndex().toString().padStart(2, '0'));

  goToPreviousDrill(): void {
    this.store.goBackToPreviousDrill();
  }

  finishCurrentDrill(): void {
    void this.router.navigate(['/', AppRoutes.SESSION, SessionRoutes.DRILL_COMPLETED]);
  }

  confirmAbort(): void {
    this.confirmDialog
      .confirm$({
        header: this.translate.instant('session.active.abortConfirm'),
        okLabel: this.translate.instant('session.active.abortDiscardCta'),
        cancelLabel: this.translate.instant('session.active.abortContinueCta'),
        destructive: true
      })
      .pipe(
        filter((ok: boolean): boolean => ok),
        take(1)
      )
      .subscribe((): void => this.abortDiscard());
  }

  abortDiscard(): void {
    this.haptics.impact('light');
    this.store.clear();
    void this.router.navigate(['/', AppRoutes.DRILLS]);
  }
}
